import React, { useState, useRef, useEffect } from "react";
import {
  SiNestjs,
  SiPrisma,
  SiJest,
  SiGithub,
  SiGraphql,
  SiRedis,
  SiPostgresql,
  SiMongodb,
  SiExpress,
  SiNextdotjs,
  SiMapbox,
  SiSocketdotio,
  SiTypescript,
  SiReact,
} from "react-icons/si";
import {
  FaNetworkWired,
  FaDatabase,
  FaServer,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";

interface Backend {
  aws: string[];
  cache: string[];
  api: string[];
  database: string[];
  entities: string[];
  security: string[];
}

interface Management {
  courses: string[];
  rbac: string[];
  instructor: string[];
  institute: string[];
}

interface Project {
  title: string;
  description: string;
  summary: string;
  technologies: Array<{
    name: string;
    icon: JSX.Element;
  }>;
  features?: string[];
  githubLink?: string;
  components?: string[];
  highlights?: string[];
  management?: Management;
  backend?: Backend;
  website?: string;
  image?: string;
}

const Projects: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToSlide = (index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const card = container.children[index] as HTMLElement;
      if (card) {
        const gap = 24; // gap-6 = 1.5rem = 24px
        const scrollPosition = card.offsetLeft - gap;
        container.scrollTo({ left: scrollPosition, behavior: "smooth" });
        setCurrentIndex(index);
      }
    }
  };

  const nextSlide = () => {
    const next = (currentIndex + 1) % projects.length;
    scrollToSlide(next);
  };

  const prevSlide = () => {
    const prev = (currentIndex - 1 + projects.length) % projects.length;
    scrollToSlide(prev);
  };

  // Update current index on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const cardWidth = container.children[0]?.clientWidth || 0;
      const gap = 24;
      const scrollPos = container.scrollLeft;
      const newIndex = Math.round(scrollPos / (cardWidth + gap));
      if (
        newIndex !== currentIndex &&
        newIndex >= 0 &&
        newIndex < container.children.length
      ) {
        setCurrentIndex(newIndex);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [currentIndex]);

  const projects: Project[] = [
    {
      title: "FiveOne Dating App",
      description:
        "A unique dating app that matches five male users with one female user in real-time chat rooms.",
      summary: "Built real-time matchmaking backend with advanced algorithms connecting 5 male users with 1 female user. Implemented WebSocket architecture using Socket.IO for instant messaging and role-based chat room management. Designed efficient MongoDB schema for user profiles and chat history, with Redis caching for optimal performance. Scaled to support thousands of concurrent users.",
      image: "/projects/fiveone.png",
      website: "https://fiveone.app/",
      technologies: [
        { name: "NestJS", icon: <SiNestjs className="text-red-600" /> },
        { name: "MongoDB", icon: <SiMongodb className="text-green-500" /> },
        {
          name: "WebSocket",
          icon: <SiSocketdotio className="text-blue-400" />,
        },
        {
          name: "TypeScript",
          icon: <SiTypescript className="text-blue-400" />,
        },
        { name: "Redis", icon: <SiRedis className="text-red-500" /> },
      ],
    },
    {
      title: "Stock Market Portal",
      description:
        "A scalable stock management platform with real-time data handling and optimized user experience.",
      summary: "Built scalable RESTful APIs with Express.js and migrated 70% of frontend from React to Next.js 14 for better SEO and performance. Implemented real-time stock data processing, portfolio management, and user authentication with role-based access. Optimized with SSR and incremental static regeneration.",
      technologies: [
        { name: "Next.js", icon: <SiNextdotjs className="text-black" /> },
        { name: "React.js", icon: <SiReact className="text-blue-600" /> },
        { name: "Express.js", icon: <SiExpress className="text-gray-700" /> },
        { name: "SQL", icon: <FaDatabase className="text-blue-700" /> },
      ],
      website: "https://www.sharesanskar.com",
    },
    {
      title: "Laliguras Hiking Platform",
      description:
        "A comprehensive hiking platform for booking tour guides, lodges, and exploring hiking locations.",
      summary: "Built booking management system with interactive ArcGIS maps for hiking locations. Created RESTful APIs with Express.js and designed responsive UI with Next.js. Implemented tour guide and lodge reservation system with real-time availability tracking and integrated secure payment gateway.",
      technologies: [
        { name: "Next.js", icon: <SiNextdotjs className="text-white" /> },
        { name: "Express.js", icon: <SiExpress className="text-white" /> },
        { name: "MongoDB", icon: <SiMongodb className="text-green-500" /> },
        { name: "ArcGIS", icon: <SiMapbox className="text-blue-400" /> },
      ],
      website: "https://www.laliguras.de/",
    },
    {
      title: "Chatter App Backend",
      description:
        "Real-time chat and video call application backend with WebSocket communication and message queuing.",
      summary: "Built real-time chat backend with WebSocket using NestJS and Socket.IO. Integrated video call functionality and implemented message queuing system with BullMQ. Designed efficient PostgreSQL schema with Redis caching for real-time features and optimized performance with caching strategies.",
      technologies: [
        { name: "NestJS", icon: <SiNestjs className="text-red-600" /> },
        {
          name: "PostgreSQL",
          icon: <SiPostgresql className="text-blue-400" />,
        },
        { name: "Redis", icon: <SiRedis className="text-red-500" /> },
        {
          name: "WebSocket",
          icon: <FaNetworkWired className="text-green-500" />,
        },
        { name: "BullMQ", icon: <FaServer className="text-yellow-500" /> },
      ],
      githubLink: "https://github.com/roshankc00/Chatter-app-backend",
    },
    {
      title: "Food Delivery System Microservice",
      description:
        "Comprehensive food delivery system built with microservices architecture using TCP communication.",
      summary: "Architected microservices system with separate services for authentication, notifications, food management, order processing, and shopping cart. Implemented TCP communication between services using NestJS and Prisma ORM. Built with Jest for testing and scalable service design.",
      technologies: [
        { name: "NestJS", icon: <SiNestjs className="text-red-600" /> },
        { name: "Prisma", icon: <SiPrisma className="text-blue-600" /> },
        { name: "Jest", icon: <SiJest className="text-red-700" /> },
      ],
      githubLink: "https://github.com/roshankc00/Food_delivery_system",
    },
    {
      title: "Mero Bazar - E-commerce CMS",
      description:
        "Sophisticated multi-vendor e-commerce CMS platform with comprehensive product management and analytics.",
      summary: "Built multi-vendor e-commerce platform with GraphQL API using NestJS and Prisma. Implemented product management, shopping cart, order tracking, and sales analytics dashboard. Designed user authentication and store management system with activity tracking.",
      technologies: [
        { name: "NestJS", icon: <SiNestjs className="text-red-600" /> },
        { name: "GraphQL", icon: <SiGraphql className="text-pink-600" /> },
        { name: "Prisma", icon: <SiPrisma className="text-blue-600" /> },
      ],
      githubLink: "https://github.com/roshankc00/Mero-Bazar",
    },
  ];

  React.useEffect(() => {
    AOS.init();
  }, []);

  return (
    <section
      id="projects"
      className="relative py-20 bg-[#0a0a0f] border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex items-center justify-between" data-aos="fade-up">
          <div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-3">
              Projects
            </h2>
            <p className="text-gray-400 text-lg">Selected work and collaborations</p>
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              className="p-3 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
              aria-label="Previous"
            >
              <FaChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextSlide}
              className="p-3 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
              aria-label="Next"
            >
              <FaChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Carousel container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {projects.map((project, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-full md:w-[calc(50%-12px)] snap-start"
            >
              <div className="group relative bg-white/5 border border-white/10 rounded-lg p-6 md:p-8 overflow-hidden transition-all duration-300 hover:border-white/20 hover:bg-white/10 h-full">
                {/* Content */}
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl md:text-3xl font-medium text-white group-hover:text-blue-400 transition-colors duration-300 flex-1">
                      {project.title}
                    </h3>
                    <div className="flex gap-2 ml-4">
                      {project.website && (
                        <a
                          href={project.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
                          aria-label="Visit website"
                        >
                          <FaExternalLinkAlt className="w-4 h-4" />
                        </a>
                      )}
                      {project.githubLink && (
                        <a
                          href={project.githubLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
                          aria-label="View on GitHub"
                        >
                          <SiGithub className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-400 mb-6 leading-relaxed text-sm md:text-base">
                    {project.description}
                  </p>

                  {project.summary && (
                    <p className="text-gray-400 mb-6 leading-relaxed text-sm md:text-base">
                      {project.summary}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-gray-400"
                      >
                        <span className="text-base">{tech.icon}</span>
                        <span>{tech.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${currentIndex === index
                ? "bg-white w-8"
                : "bg-white/20 hover:bg-white/40"
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
